import withDisplayName from 'bz-utilities/hoc/display-name';
import * as Immutable from 'immutable';
import _ from 'lodash';
import { action, computed, observable, toJS } from 'mobx';
import { observer } from 'mobx-react';
import { CompositeDecorator, EditorState, Entity, NestedUtils, RichUtils } from 'tm2-draft-js';

type TControls<T = any> =
  | { control: React.ComponentType<T> }
  | { control: { default: React.ComponentType<T> } };
const exampleAiField = [
  'C.admissionDate',
  'T.data.radiations/alteredSensation/weakness:-b0924dc6-05b8-4a5e-917d-62c30355fa9b'
];

class BaseStore {
  @observable editor = null;
  @observable controls: Record<string, TControls> = {};
  @observable debug = false;
  @observable template = '{}';

  dataAsMap = observable.map();
  @observable cache = {};
  @observable styleMap: Record<string, React.CSSProperties> = {};

  rawTemplate = observable.map();
  @observable readonly aiFilledFields = observable.set<string>();
  @observable clonedConsultation: boolean = false;

  constructor(controls) {
    this.initialize(controls);
  }

  @computed // hack as this is used by lots of clinical components
  get data() {
    return toJS(this.dataAsMap);
  }

  @action
  initialize(controls) {
    this.controls = controls || {};
    this.editor = EditorState.createEmpty();

    this.styleMap['colour-black'] = { color: 'rgba(0, 0, 0, 1.0)' };
    this.styleMap['colour-red'] = { color: 'rgba(255, 0, 0, 1.0)' };
    this.styleMap['colour-orange'] = { color: 'rgba(255, 127, 0, 1.0)' };
    this.styleMap['colour-yellow'] = { color: 'rgba(180, 180, 0, 1.0)' };
    this.styleMap['colour-green'] = { color: 'rgba(0, 180, 0, 1.0)' };
    this.styleMap['colour-blue'] = { color: 'rgba(0, 0, 255, 1.0)' };
  }

  @computed
  get blockRenderMap() {
    return NestedUtils.DefaultBlockRenderMap.merge(
      new Immutable.OrderedMap({
        'group-container': {
          element: 'div',
          editable: true,
          nestingEnabled: true
        },
        'group-item': {
          element: 'li',
          editable: true,
          nestingEnabled: true,
          wrapper: <ul className="group-items" contentEditable={this.mode !== 'live'} />
        },
        insert: { element: 'div', editable: true, nestingEnabled: true }
      })
    );
  }

  @computed
  get formulaVariables(): Record<string, string | number> {
    let entityMap = this.rawTemplate.get('entityMap');
    return Object.values(entityMap || {})
      .filter((entity) => !!entity.data.formulakey)
      .reduce((result, entity) => {
        let value = this.dataAsMap.get(entity.data.field);
        if (!!value) {
          switch (entity.type) {
            case 'Control-YesNo':
            case 'Control-Options':
              value = JSON.parse(value);
              if (Array.isArray(value)) {
                if (value.length === 1 && !entity.data.multi) {
                  const num = parseFloat(value[0]);
                  value = isNaN(num) ? value[0] : num;
                } else {
                  value = value.map((x) => {
                    const num = parseFloat(x);
                    return isNaN(num) ? x : num;
                  });
                  value = this.getFormulaVariableValue(value);
                }
              }
              break;
            case 'Control-DatePicker':
              value = value;
              break;
            default:
              value = this.getFormulaVariableValue(value);
              break;
          }
        }
        result[entity.data.formulakey] = value;
        return result;
      }, {});
  }

  getFormulaVariableValue(value) {
    try {
      let v = typeof value === 'string' ? JSON.parse(value) : value;

      if (Array.isArray(v)) {
        return v
          .filter((x) => {
            return !isNaN(parseFloat(x));
          })
          .reduce(
            (accumulator, currentValue) => parseFloat(accumulator) + parseFloat(currentValue),
            0
          );
      } else {
        var int = parseFloat(value);
        if (!isNaN(int)) {
          return int;
        }
      }
    } catch (e) {
      return value.toString();
    }
  }

  updateTemplate() {
    this.rawTemplate.replace(this.onExport());
  }

  decoratorFn() {
    let control = {
      strategy: (block, callback) => {
        block.findEntityRanges((character) => {
          if (character.hasStyle('MASK')) {
            return false;
          }
          let entityKey = character.getEntity();
          if (!entityKey) {
            return false;
          }
          return Entity.get(entityKey).getType().startsWith('Control');
        }, callback);
      },

      component: observer(
        withDisplayName((props: any) => {
          let { entityKey, offsetKey, children } = props;

          let entity = Entity.get(entityKey);

          let attributes = entity.getData();
          let type = entity.getType();
          let schema = this.controls[type.substring(8) || ''];

          if (!schema) {
            return (
              <span>
                <i className="fa fa-question-circle" />
              </span>
            );
          }

          let blockKey = offsetKey.substring(0, offsetKey.indexOf('-'));

          let properties = {
            key: entityKey,
            blockKey: blockKey,
            field: (attributes.field || '').trim(),
            control: schema.control,
            children: children,
            attributes: attributes,
            inline:
              attributes.inline ||
              type === 'Control-Label' ||
              type === 'Control-DateTimePicker' ||
              false
          };

          return this.controlFn(properties);
        }, 'BaseStoreControlWrapper')
      )
    };
    return new CompositeDecorator([control]);
  }

  @action
  onRefresh(filter) {
    let { editor } = this;
    let content = editor.getCurrentContent();
    let selection = editor.getSelection();

    let filterFn =
      filter ||
      ((character) => {
        return !!character.getEntity();
      });

    let callback = (key, start, end) => {
      let highlight = selection.merge({
        focusKey: key,
        anchorKey: key,
        focusOffset: end,
        anchorOffset: start
      });

      editor = EditorState.forceSelection(editor, highlight);
      editor = RichUtils.toggleInlineStyle(editor, 'REFRESH');
    };

    content.getBlockMap().forEach((block, blockKey) => {
      block.findEntityRanges(filterFn, callback.bind(this, blockKey));
    });

    this.editor = EditorState.acceptSelection(editor, selection);
  }

  controlFn(props) {
    return <span>No Control Function</span>;
  }
}

export default BaseStore;
